<template>
  <div>
    <!-- 이벤트 타임라인 -->
    <highcharts class="export-pdf-heatmap-chart" :options="chartOptions" v-show="!exportingPdfForIe" ref="highchart" />
    <img v-show="exportingPdfForIe" class="export-pdf-heatmap-chart" />
    <!-- 이벤트 상세 정보 -->
    <div class="mr-t10 title-bar">
      <span class="tit content-title">이벤트 상세 정보</span>
      <el-button style="height:19px;min-width:19px;width:22px;padding:2px 5px;" @click="showDetail = !showDetail">
        <i :class="showDetail ? 'el-icon-arrow-down' : 'el-icon-arrow-up'" style="margin-top:-2px;"/>
      </el-button>
    </div>
    <el-table-wrapper v-show="showDetail" ref="table" v-loading="tableLoading" :data="tableData" :columns="tableColumns" :column-default="columnDefault" :pagination="false">
      <template slot-scope="scope" slot="eventlevel-slot">
        <w-eventlevel-cell :eventlevel-id="scope.row.eventlevelId" />
      </template>
      <template slot-scope="scope" slot="event-message-slot">
        <div v-if="scope.row.dynamicThresholdFlag === 'Y'" class="round-box-info-text red">
          동적
        </div>
        <span class="link c-pointer" @click="clickEventMessage(scope.row)">{{ scope.row.eventMessage }}</span>
      </template>
    </el-table-wrapper>
    <w-obj-event-hist-popup ref="eventHistPopup" />
  </div>
</template>

<script>
import { isNil, cloneDeep, forEach } from 'lodash';
import moment from 'moment';
import { columnDefault } from '@/constants/table';
import MsgUtil from '@/utils/message';
import eventHistApi from '@/api/event/event-hist';
import { getDateToString } from '@/utils/date';
import { getValueAfterCheckEmpty } from '@/utils/string';
import { openFaultAnalysisPopup } from '@/utils/event';
import WEventlevelCell from '@/components/table/EventlevelCell';
import WObjEventHistPopup from '../popup/ObjEventHistPopup';
import { getEventPeriodCondition, getDateLabel } from '../utils/fault-analysis';

const lineColor = '#e6e6e6';
const defaultChartOptions = {
  chart: {
    type: 'heatmap',
    height: 35,
    backgroundColor: 'transparent',
  },
  title: false,
  credits: false,
  xAxis: {
    title: false,
    categories: [],
    lineColor,
    labels: {
      enabled: false,
    },
    tickWidth: 0,
  },
  yAxis: [
    {
      title: false,
      categories: [],
      lineColor,
      lineWidth: 1,
    },
    {
      title: false,
      categories: [],
      lineColor,
      lineWidth: 1,
      opposite: true,
      labels: {
        enabled: false,
      },
    },
  ],
  colorAxis: {
    min: 0,
    minColor: '#FFFFFF',
    maxColor: '#FFFFFF',
  },
  legend: {
    enabled: false,
  },
  tooltip: {
    useHTML: true,
    formatter() {
      if (isNil(this.point.eventlevelId)) {
        return false;
      }
      const title = isNil(this.point.ipaddress) || this.point.ipaddress === '' ? this.point.objName : `${this.point.objName}(${this.point.ipaddress})`;
      let tooltip = `<div><span>${title}</span><span style="margin-left:8px;color:#909399;">${getDateToString(this.point.checkDatetime)}</span></div><ul><li><b>이벤트 발생 건수: </b>${this.point.value}건</li>`;
      if (!isNil(this.point.eventlevelId)) {
        tooltip += `<li><b>최고 이벤트 등급: </b><span class="eventlevel-tag" style="color:${this.point.eventlevelColorText}; background-color:${this.point.color};">${this.point.eventlevelName}</span></li>`;
      }
      return `${tooltip}</ul>`;
    },
  },
  series: [
    {
      borderWidth: 1,
      turboThreshold: 10000,
      borderColor: lineColor,
      data: [],
      dataLabels: {
        enabled: false,
      },
    },
  ],
};

/**
 * [장애 분석] 연관 이벤트 정보 컴포넌트
 * @author Ju
 */
export default {
  name: 'WRelatedEventInfoView',
  components: {
    WObjEventHistPopup,
    WEventlevelCell,
  },
  props: {
    seqNumber: {
      type: Number,
    },
    exportingPdfForIe: {
      type: Boolean,
    },
  },
  data() {
    return {
      // 히트맵 기본 옵션
      defaultChartOptions,
      // 히트맵 바인드용 옵션
      chartOptions: defaultChartOptions,
      // 이벤트 상세 정보 show/hidden 여부
      showDetail: false,
      // 컬럼 기본 설정
      columnDefault,
      // 이벤트 발생 장비 및 연관 장비 목록
      objList: [],
      // 분석 이벤트 이력 데이터
      eventHistData: null,
      // 이벤트 이력 컬럼
      tableColumns: [
        {
          prop: 'eventlevelName',
          label: '등급',
          width: 76,
          scopedSlot: 'eventlevel-slot',
          headerAlign: 'center',
          columnKey: 'eventlevelId',
          searchable: true,
          searchType: 'eventlevel',
          remoteProp: 'eventlevelId',
        },
        {
          prop: 'objName',
          label: '대상',
          width: 250,
          headerAlign: 'center',
          formatter: this.formatterForTarget,
          searchable: true,
          remoteProp: 'targetInfo',
        },
        {
          prop: 'rsctypeName',
          label: '자원구분',
          width: 120,
          headerAlign: 'center',
          searchable: true,
        },
        {
          prop: 'eventMessage',
          label: '장애메시지',
          searchable: true,
          scopedSlot: 'event-message-slot',
        },
        {
          prop: 'checkDatetime',
          label: '발생시각',
          width: 160,
          searchable: true,
          searchType: 'date',
          formatter: this.formatterForDate,
        },
      ],
      // 이벤트 이력 테이블 로딩 여부
      tableLoading: false,
      // 이벤트 이력 테이블 데이터 로드 완료 여부
      tableLoaded: false,
      // 이벤트 이력 데이터
      tableData: [],
    };
  },
  watch: {
    seqNumber: {
      immediate: true,
      handler(to) {
        if (!isNil(to)) {
          this.loadEventTimelineData();
        } else {
          this.reset();
        }
      },
    },
    showDetail: {
      handler(to) {
        if (to && this.tableLoaded === false) {
          this.loadEventHistData();
        }
      },
    },
  },
  created() {
    const me = this;
    this.defaultChartOptions.plotOptions = {
      series: {
        events: {
          click(event) {
            // console.debug(event, me);
            if (!isNil(event.point.eventlevelId)) {
              me.showEventHistPopup(me.objList[event.point.y], event.point.checkDatetime);
            }
          },
        },
      },
    };
  },
  methods: {
    /**
     * 이벤트 발생 장비 및 연관 장비의 이벤트 타임라인 정보를 조회한다.
     * @author Ju
     * @return {[type]} [description]
     */
    loadEventTimelineData() {
      if (isNil(this.seqNumber)) {
        return;
      }
      eventHistApi
        .getFaultAnalysisEventTimeline(this.seqNumber)
        .then((response) => {
          if (response.data.success) {
            const data = response.data.data;
            const options = cloneDeep(this.defaultChartOptions);
            if (!isNil(data)) {
              options.xAxis.categories = data.dateCategories;
              options.yAxis[0].categories = data.objCategories;
              options.series[0].data = data.chartData;
              this.objList = data.objList;
              options.chart.height += this.objList.length * 20;
              this.eventHistData = data.eventHistData;
            } else {
              this.objList = [];
            }
            this.chartOptions = options;
            this.$nextTick(() => {
              forEach(this.$refs.highchart.chart.series[0].data, (point) => {
                if (!isNil(point.options.eventlevelId)) {
                  point.graphic.attr({
                    cursor: 'pointer',
                  });
                }
              });
            });
          } else {
            this.$message(MsgUtil.getResultMsgData(MsgUtil.action.VIEW, '이벤트 타임라인 정보', false));
          }
        })
        .catch((e) => {
          this.$message(MsgUtil.getResultMsgData(MsgUtil.action.VIEW, '이벤트 타임라인 정보', false));
          throw new Error(e);
        });
    },
    /**
     * 선택한 기간에 대한 장비 상세 이벤트 이력 팝업을 연다.
     * @author Ju
     * @param  {[type]} obj           [description]
     * @param  {[type]} checkDatetime [description]
     */
    showEventHistPopup(obj, checkDatetime) {
      const endDatetime = moment(checkDatetime)
        .add(59, 's')
        .toDate()
        .getTime();
      this.$refs.eventHistPopup.open(obj, checkDatetime, endDatetime);
    },
    /**
     * 이벤트 상세 이력을 조회한다.
     * @author Ju
     */
    loadEventHistData() {
      const objIds = [];
      forEach(this.objList, (obj) => {
        objIds.push(obj.objId);
      });
      const period = getEventPeriodCondition(this.eventHistData.checkDatetime);
      const startDatetime = getDateLabel(period.startDatetime);
      const endDatetime = getDateLabel(period.endDatetime);
      const params = {
        objIds,
        startDatetime,
        endDatetime,
      };
      this.tableLoading = true;
      eventHistApi
        .getFaultAnalysisEventHist(params)
        .then((response) => {
          if (response.data.success) {
            this.tableLoaded = true;
            this.tableData = response.data.data;
          } else {
            this.$message(MsgUtil.getResultMsgData(MsgUtil.action.VIEW, '이벤트 이력 정보', false));
          }
        })
        .catch((e) => {
          this.$message(MsgUtil.getResultMsgData(MsgUtil.action.VIEW, '이벤트 이력 정보', false));
          throw new Error(e);
        })
        .finally(() => {
          this.tableLoading = false;
        });
    },
    /**
     * 날짜 formatter
     * @author Ju
     * @param  {[type]} row    [description]
     * @param  {[type]} column [description]
     * @param  {[type]} value  [description]
     * @return {[type]}        [description]
     */
    formatterForDate(row, column, value) {
      return getDateToString(value);
    },
    /**
     * 대상 정보를 반환한다.
     * 수정 시 다음 vue도 함께 수정한다.
     * → 동일한 함수 쓰는 곳: SmartAnalyzerPopup, EventTicketAddPopup
     *
     * @author Ju
     * @param  {[type]} row    [description]
     * @param  {[type]} column [description]
     * @param  {[type]} value  [description]
     * @return {[type]}        [description]
     */
    formatterForTarget(row, column, value) {
      if (row.objDefineId !== 'SERVICE' && row.objDefineId !== 'E2E_SERVICE' && row.objDefineId !== 'FMS_SENSOR') {
        return `${getValueAfterCheckEmpty(value)} (${getValueAfterCheckEmpty(row.ipaddress)})`;
      }
      return getValueAfterCheckEmpty(value);
    },
    /**
     * 장애 메시지 클릭 시 해당 이벤트의 관리 대상의 성능운영 > 요약 화면으로 이동한다.
     * @author Ju
     * @param  {[type]} row [description]
     * @return {[type]}     [description]
     */
    clickEventMessage(row) {
      openFaultAnalysisPopup(row.seqNumber);
    },
    /**
     * 화면을 초기화한다.
     * @author Ju
     */
    reset() {
      this.eventHistData = null;
      this.objList = [];
      this.chartOptions = this.defaultChartOptions;
      this.tableData = [];
      this.tableLoaded = false;
      this.showDetail = false;
    },
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/components/popup/FaultAnalysisPopup/components/RelatedEventInfoView.vue